import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  adminApi,
  clearAdminToken,
  persistAdminToken,
  readAdminToken,
} from "@/lib/adminApi";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = readAdminToken();
    if (!stored) {
      setLoading(false);
      return;
    }
    // Verify the stored token is still valid against the backend.
    adminApi
      .get("/admin/auth/check")
      .then(() => setToken(stored))
      .catch(() => {
        clearAdminToken();
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const signIn = useCallback(async (username, password) => {
    const { data } = await adminApi.post("/admin/auth/login", {
      username,
      password,
    });
    persistAdminToken(data.token, data.expires_at);
    setToken(data.token);
    return data;
  }, []);

  const signOut = useCallback(() => {
    clearAdminToken();
    setToken(null);
  }, []);

  const value = useMemo(
    () => ({
      token,
      isAuthed: !!token,
      loading,
      signIn,
      signOut,
    }),
    [token, loading, signIn, signOut],
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside <AdminAuthProvider>");
  return ctx;
}
